import { useState } from "react";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

export function CompletenessBadge({ completeness: c, compact = false }) {
  const [open, setOpen] = useState(false);
  if (!c) return null;
  if (c.complete) {
    return compact ? (
      <span title="All required info complete" data-testid="completeness-complete">
        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full" data-testid="completeness-complete">
        <CheckCircle2 className="h-3 w-3" /> Info complete
      </span>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className={`inline-flex items-center gap-1 rounded-full font-semibold text-amber-800 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors ${compact ? "text-[11px] px-1.5 py-0.5" : "text-xs px-2 py-0.5"}`}
          data-testid="completeness-badge"
          title="Missing required info"
        >
          <AlertTriangle className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
          {c.missing.length} missing
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" onClick={(e) => e.stopPropagation()} data-testid="completeness-popover">
        <MissingList c={c} />
      </PopoverContent>
    </Popover>
  );
}

function MissingList({ c }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-slate-700">Data completeness</p>
        <span className="text-xs font-bold text-amber-700">{c.percent}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-2.5">
        <div className="h-full bg-amber-500 rounded-full" style={{ width: `${c.percent}%` }} />
      </div>
      {c.missing_core?.length > 0 && (
        <div className="mb-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-red-500 mb-1">Core (required)</p>
          <div className="flex flex-wrap gap-1">
            {c.missing_core.map((m) => <span key={m} className="text-[11px] bg-red-50 text-red-700 border border-red-100 px-1.5 py-0.5 rounded">{m}</span>)}
          </div>
        </div>
      )}
      {c.missing_additional?.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 mb-1">Needed before Qualified / beyond Quotation</p>
          <div className="flex flex-wrap gap-1">
            {c.missing_additional.map((m) => <span key={m} className="text-[11px] bg-amber-50 text-amber-800 border border-amber-100 px-1.5 py-0.5 rounded">{m}</span>)}
          </div>
        </div>
      )}
      {c.legacy_exempt && <p className="text-[10px] text-slate-400 mt-2">Imported / legacy record — flagged, not blocked. Please complete when possible.</p>}
    </div>
  );
}

export function CompletenessCard({ completeness: c, onComplete, entityLabel = "record" }) {
  if (!c || c.complete) return null;
  return (
    <Card className="p-5 border-amber-300 bg-amber-50/50" data-testid="completeness-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-sm text-slate-800 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" /> Missing required information ({c.percent}% complete)
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            {c.legacy_exempt
              ? `This imported ${entityLabel} is missing required fields — please fill them in when you can.`
              : `This ${entityLabel} cannot progress (Qualified / beyond Quotation) until the fields below are filled.`}
          </p>
          <div className="mt-3"><MissingList c={c} /></div>
        </div>
      </div>
      {onComplete && (
        <Button size="sm" onClick={onComplete} className="mt-3 bg-amber-600 hover:bg-amber-700 text-white" data-testid="complete-info-btn">
          Complete now
        </Button>
      )}
    </Card>
  );
}
